import React, { useState, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import { getApiBase } from "../api";

export default function BrokerCreditCheck() {
  const apiBase = useMemo(() => getApiBase(), []);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [brokerData, setBrokerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function searchBrokers() {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${apiBase}/broker-credit/search/${encodeURIComponent(searchQuery)}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.brokers || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadBrokerProfile(brokerId) {
    setSelectedBroker(brokerId);
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${apiBase}/broker-credit/${brokerId}`);
      if (res.ok) {
        const data = await res.json();
        setBrokerData(data);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function getRatingColor(rating) {
    const colors = { A: '#10b981', B: '#3b82f6', C: '#f59e0b', D: '#f97316', F: '#ef4444' };
    return colors[rating] || '#6b7280';
  }

  function getRatingBg(rating) {
    const colors = { A: '#d1fae5', B: '#dbeafe', C: '#fef3c7', D: '#ffedd5', F: '#fee2e2' };
    return colors[rating] || '#f3f4f6';
  }

  function formatCurrency(cents) {
    if (!cents) return '$0';
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString();
  }

  function StarRating({ rating, size = 16 }) {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} style={{ color: i <= rating ? '#f59e0b' : '#d1d5db', fontSize: size }}>★</span>
      );
    }
    return <span>{stars}</span>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <PageHeader 
        title="🔍 Broker Credit Check" 
        subtitle="Research broker payment history and reliability before accepting loads"
      />

      {error && (
        <div style={{ padding: 12, background: "#fee2e2", borderRadius: 8, marginBottom: 16, color: "#991b1b" }}>
          {error}
        </div>
      )}

      {/* Search Box */}
      <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 20, marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 12px 0", fontWeight: 700 }}>Search Brokers</h3>
        <div style={{ display: "flex", gap: 12 }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchBrokers()}
            placeholder="Enter broker name or MC number..."
            style={{ flex: 1, padding: 12, borderRadius: 8, border: "1px solid #d1d5db", fontSize: 15 }}
          />
          <button
            onClick={searchBrokers}
            disabled={loading || !searchQuery.trim()}
            style={{
              padding: "12px 24px",
              background: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            {loading ? "..." : "🔍 Search"}
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
              Found {searchResults.length} broker(s)
            </div>
            {searchResults.map(broker => (
              <div
                key={broker.id}
                onClick={() => loadBrokerProfile(broker.id)}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  marginBottom: 8,
                  cursor: "pointer",
                  background: selectedBroker === broker.id ? "#eff6ff" : "white",
                  display: "flex",
                  alignItems: "center",
                  gap: 16
                }}
              >
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  background: getRatingBg(broker.creditRating),
                  color: getRatingColor(broker.creditRating),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 20
                }}>
                  {broker.creditRating || '?'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{broker.name}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    {broker.avgDaysToPay ? `Avg ${broker.avgDaysToPay.toFixed(0)} days to pay` : 'No payment data'}
                    {broker.totalLoadsCompleted > 0 && ` • ${broker.totalLoadsCompleted} loads`}
                    {broker.isVerified && ' • ✓ Verified'}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: "#2563eb" }}>View Details →</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Broker Profile */}
      {brokerData && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {/* Left Column - Credit Profile */}
          <div>
            {/* Credit Score Card */}
            <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 20, marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: 12,
                  background: getRatingBg(brokerData.creditProfile?.creditRating),
                  color: getRatingColor(brokerData.creditProfile?.creditRating),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 36
                }}>
                  {brokerData.creditProfile?.creditRating || '?'}
                </div>
                <div>
                  <h2 style={{ margin: 0, fontWeight: 700 }}>{brokerData.broker?.name}</h2>
                  <div style={{ color: "#6b7280", marginTop: 4 }}>
                    Credit Score: {brokerData.creditProfile?.creditScore || 'N/A'}/100
                    {brokerData.creditProfile?.isVerified && (
                      <span style={{ marginLeft: 8, color: "#10b981" }}>✓ Verified</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Metrics */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ padding: 16, background: "#f9fafb", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#2563eb" }}>
                    {brokerData.creditProfile?.avgDaysToPay?.toFixed(0) || '—'}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Avg Days to Pay</div>
                </div>
                <div style={{ padding: 16, background: "#f9fafb", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#10b981" }}>
                    {brokerData.creditProfile?.onTimePaymentRate?.toFixed(0) || '—'}%
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>On-Time Rate</div>
                </div>
                <div style={{ padding: 16, background: "#f9fafb", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#374151" }}>
                    {brokerData.creditProfile?.totalLoadsCompleted || 0}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Loads Completed</div>
                </div>
                <div style={{ padding: 16, background: "#f9fafb", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#374151" }}>
                    {formatCurrency(brokerData.creditProfile?.totalAmountPaid)}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Total Paid</div>
                </div>
              </div>

              {/* Bond & Insurance */}
              {(brokerData.creditProfile?.bondAmount || brokerData.creditProfile?.insuranceAmount) && (
                <div style={{ marginTop: 16, padding: 12, background: "#f0fdf4", borderRadius: 8 }}>
                  <div style={{ fontSize: 13 }}>
                    {brokerData.creditProfile?.bondAmount && (
                      <span>💰 Bond: {formatCurrency(brokerData.creditProfile.bondAmount)}</span>
                    )}
                    {brokerData.creditProfile?.insuranceAmount && (
                      <span style={{ marginLeft: 16 }}>🛡️ Insurance: {formatCurrency(brokerData.creditProfile.insuranceAmount)}</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Days to Pay Distribution */}
            {brokerData.daysToPayDistribution?.length > 0 && (
              <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 20 }}>
                <h3 style={{ margin: "0 0 16px 0", fontWeight: 700 }}>📊 Payment Speed Distribution</h3>
                {brokerData.daysToPayDistribution.map((item, i) => {
                  const maxCount = Math.max(...brokerData.daysToPayDistribution.map(d => Number(d.count)));
                  const pct = maxCount > 0 ? (Number(item.count) / maxCount) * 100 : 0;
                  const colors = ['#10b981', '#3b82f6', '#f59e0b', '#f97316', '#ef4444'];
                  return (
                    <div key={i} style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                        <span>{item.range}</span>
                        <span style={{ fontWeight: 600 }}>{item.count}</span>
                      </div>
                      <div style={{ height: 8, background: "#e5e7eb", borderRadius: 4 }}>
                        <div style={{ 
                          height: "100%", 
                          width: `${pct}%`, 
                          background: colors[i] || '#6b7280',
                          borderRadius: 4 
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Column - Reviews & History */}
          <div>
            {/* Reviews Summary */}
            {brokerData.reviewStats?.totalReviews > 0 && (
              <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 20, marginBottom: 20 }}>
                <h3 style={{ margin: "0 0 16px 0", fontWeight: 700 }}>⭐ Carrier Reviews</h3>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <div style={{ fontSize: 36, fontWeight: 700 }}>
                    {Number(brokerData.reviewStats.avgOverallRating).toFixed(1)}
                  </div>
                  <div>
                    <StarRating rating={Math.round(brokerData.reviewStats.avgOverallRating)} size={20} />
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      {brokerData.reviewStats.totalReviews} reviews
                    </div>
                  </div>
                  {brokerData.reviewStats.wouldWorkAgainCount > 0 && (
                    <div style={{ marginLeft: "auto", padding: "6px 12px", background: "#d1fae5", borderRadius: 20, fontSize: 12, color: "#065f46" }}>
                      👍 {Math.round((brokerData.reviewStats.wouldWorkAgainCount / brokerData.reviewStats.totalReviews) * 100)}% would work again
                    </div>
                  )}
                </div>

                {/* Rating Breakdown */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                  {brokerData.reviewStats.avgPaymentRating && (
                    <div style={{ padding: 8, background: "#f9fafb", borderRadius: 6, textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>Payment</div>
                      <StarRating rating={Math.round(brokerData.reviewStats.avgPaymentRating)} size={14} />
                    </div>
                  )}
                  {brokerData.reviewStats.avgCommunicationRating && (
                    <div style={{ padding: 8, background: "#f9fafb", borderRadius: 6, textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>Communication</div>
                      <StarRating rating={Math.round(brokerData.reviewStats.avgCommunicationRating)} size={14} />
                    </div>
                  )}
                  {brokerData.reviewStats.avgLoadAccuracyRating && (
                    <div style={{ padding: 8, background: "#f9fafb", borderRadius: 6, textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>Load Accuracy</div>
                      <StarRating rating={Math.round(brokerData.reviewStats.avgLoadAccuracyRating)} size={14} />
                    </div>
                  )}
                </div>

                {/* Recent Reviews */}
                {brokerData.recentReviews?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Recent Reviews</div>
                    {brokerData.recentReviews.slice(0, 5).map(review => (
                      <div key={review.id} style={{ padding: 10, background: "#f9fafb", borderRadius: 6, marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <StarRating rating={review.overallRating} size={14} />
                          <span style={{ fontSize: 11, color: "#6b7280" }}>{formatDate(review.createdAt)}</span>
                        </div>
                        {review.reviewText && (
                          <div style={{ fontSize: 13, color: "#374151" }}>{review.reviewText}</div>
                        )}
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                          — {review.carrierName || 'Anonymous'}
                          {review.wouldWorkAgain !== null && (
                            <span style={{ marginLeft: 8 }}>
                              {review.wouldWorkAgain ? '👍 Would work again' : '👎 Would not work again'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recent Payments */}
            <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 20 }}>
              <h3 style={{ margin: "0 0 16px 0", fontWeight: 700 }}>💳 Recent Payment History</h3>
              {brokerData.recentPayments?.length > 0 ? (
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f9fafb" }}>
                        <th style={{ padding: 8, textAlign: "left" }}>Date</th>
                        <th style={{ padding: 8, textAlign: "right" }}>Amount</th>
                        <th style={{ padding: 8, textAlign: "center" }}>Days</th>
                        <th style={{ padding: 8, textAlign: "center" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brokerData.recentPayments.map(payment => (
                        <tr key={payment.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                          <td style={{ padding: 8 }}>{formatDate(payment.invoiceDate)}</td>
                          <td style={{ padding: 8, textAlign: "right", fontWeight: 600 }}>
                            {formatCurrency(payment.invoiceAmountCents)}
                          </td>
                          <td style={{ padding: 8, textAlign: "center" }}>
                            {payment.daysToPay !== null ? (
                              <span style={{ 
                                color: payment.daysToPay <= 15 ? '#10b981' : 
                                       payment.daysToPay <= 30 ? '#f59e0b' : '#ef4444'
                              }}>
                                {payment.daysToPay}
                              </span>
                            ) : '—'}
                          </td>
                          <td style={{ padding: 8, textAlign: "center" }}>
                            <span style={{
                              padding: "2px 8px",
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 600,
                              background: payment.status === 'PAID' ? '#d1fae5' :
                                         payment.status === 'OVERDUE' ? '#fee2e2' : '#fef3c7',
                              color: payment.status === 'PAID' ? '#065f46' :
                                     payment.status === 'OVERDUE' ? '#991b1b' : '#92400e'
                            }}>
                              {payment.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: 20, textAlign: "center", color: "#6b7280" }}>
                  No payment history recorded yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!brokerData && searchResults.length === 0 && (
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 60, textAlign: "center" }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🔍</div>
          <h3 style={{ margin: "0 0 8px 0", fontWeight: 700 }}>Check Broker Credit Before Accepting Loads</h3>
          <p style={{ color: "#6b7280", margin: 0 }}>
            Search for any broker to see their payment history, credit rating, and reviews from other carriers.
          </p>
        </div>
      )}
    </div>
  );
}
